/** @jsxImportSource @compiled/react */
import React, { type FC } from 'react';
import { cssMap } from '@compiled/react';
// Jest uses classic JSX (`React.createElement`); TS `noUnusedLocals` does not treat JSX as reading `React`.
void React;

import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';
import Inline from '@atlaskit/primitives/inline';

import { ConfluenceSpaceAri } from '@atlassian/ari';
import { ConfluenceTeamButton } from '@atlassian/teams/confluence-team-button';

import { usePageContentId } from '@confluence/page-context';
import { getEmojiFromPublishedPageProperties } from '@confluence/emoji-title/entry-points/transformer.ts';
import {
	SpaceOverviewQuery,
	type SpaceOverviewQuery$data,
	type SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { SpaceOverviewSwitcherQuery$data } from '../__generated__/SpaceOverviewSwitcherQuery.graphql.ts';

import { RecommendedSpaceBannerLoader as RecommendedSpaceBanner } from '../../entry-points/RecommendedSpaceBanner.ts';

import { SmartSpaceOverviewCoverPicture } from './SmartSpaceOverviewCoverPicture.tsx';
// oxlint-disable-next-line import/no-cycle
import { SmartSpaceOverviewSummary } from './SmartSpaceOverviewSummary.tsx';
import { PopularQuestions } from './popular-questions/PopularQuestions.tsx';
import { SmartSpaceOverviewHeader } from './SmartSpaceOverviewHeader.tsx';
import { SmartSpaceOverviewLogo } from './SmartSpaceOverviewLogo.tsx';
import { TopLinksCard } from './top-links-card/TopLinksCard.tsx';

export type SmartSpaceOverviewData = NonNullable<
	SpaceOverviewSwitcherQuery$data['confluence_smartSpaceOverview']
>;

type SmartSpaceOverviewProps = {
	spaceKey: string;
	smartSpaceOverviewData: SmartSpaceOverviewData;
	cloudId: string;
};

export const SmartSpaceOverview: FC<SmartSpaceOverviewProps> = ({
	spaceKey,
	smartSpaceOverviewData,
	cloudId,
}) => {
	const [contentId] = usePageContentId();

	const spaceOverviewQueryResult = useQueryInterop<
		SpaceOverviewQuery$data,
		SpaceOverviewQuery$variables
	>(SpaceOverviewQuery, {
		variables: {
			spaceKey,
			excludeOperations: true,
		},
	});

	const spaceData = spaceOverviewQueryResult.data;
	const isAdminLoading = spaceOverviewQueryResult.loading;
	const isAdmin = spaceData?.space?.currentUser?.isAdmin ?? false;
	const isPersonalSpace = spaceData?.space?.type === 'personal';
	const spaceId = spaceData?.space?.id ?? '';
	const spaceName = spaceData?.space?.name ?? '';
	const emoji = getEmojiFromPublishedPageProperties(spaceData?.space?.homepage ?? null);
	const spaceIconPath = spaceData?.space?.icon?.path;
	const hasEmoji = !!emoji || !!spaceIconPath;
	const topLinks = smartSpaceOverviewData.topLinks;

	if (!contentId || !spaceKey || isAdminLoading) {
		return null;
	}

	return (
		<div css={styles.container}>
			<SmartSpaceOverviewHeader />
			<RecommendedSpaceBanner spaceKey={spaceKey} />
			<div css={styles.coverWrapper}>
				<SmartSpaceOverviewCoverPicture
					contentId={contentId}
					isAdmin={isAdmin}
					hasEmoji={hasEmoji}
				/>
				<div css={styles.logoPositioner}>
					<SmartSpaceOverviewLogo
						spaceKey={spaceKey}
						spaceId={spaceId}
						isAdmin={isAdmin}
						isPersonalSpace={isPersonalSpace}
						emoji={emoji}
						spaceIconPath={spaceIconPath}
					/>
				</div>
			</div>
			<div css={styles.contentContainer}>
				<div css={styles.titleSummarySection}>
					{spaceName ? (
						<Inline space="space.200">
							<Heading as="h1" size="xxlarge">
								{spaceName}
							</Heading>
							<ConfluenceTeamButton
								containerId={ConfluenceSpaceAri.create({
									siteId: cloudId,
									spaceId,
								}).toString()}
								numberOfTeams={1}
								orgId={cloudId}
							/>
						</Inline>
					) : null}
					<SmartSpaceOverviewSummary
						summaryData={smartSpaceOverviewData.summary}
						spaceKey={spaceKey}
					/>
				</div>
				<TopLinksCard topLinks={topLinks} spaceKey={spaceKey} spaceId={spaceId} />
				<PopularQuestions faqData={smartSpaceOverviewData.faq} spaceKey={spaceKey} />
			</div>
		</div>
	);
};

const styles = cssMap({
	container: {
		backgroundColor: token('elevation.surface'),
		padding: token('space.600'),
		paddingTop: 0,
		maxWidth: '1200px',
		margin: '0 auto',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.0'),
	},
	coverWrapper: {
		position: 'relative',
	},
	logoPositioner: {
		position: 'absolute',
		bottom: token('space.0'),
		left: token('space.400'),
		transform: 'translateY(50%)',
		zIndex: 1,
	},
	contentContainer: {
		paddingInline: token('space.400'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlockStart: '56px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
	},
	titleSummarySection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
});
